import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/combineReducer";
import {
  filterAbsenteeismRoleTabs,
  isAbsenteeismReportTab,
  isFranchiseTenant,
} from "../../utils/franchise/franchiseAbsenteeismMenu";
import RRHH from "./RRHH";
import RRHHTurns from "./RRHHTurns";
import RRHHAbsentees from "./RRHHAbsentees";
import RRHHTurnsCandidates from "./RRHHTurnsCandidates";
import RRHHTurnSRes from "./RRHHTurnSRes";
import RRHHResult from "./RRHHResult";
import ADMAbsentees from "./ADMAbsentees";
import InfoOffcanvas from '../../components/others/InfoOffCanvas/InfoOffCanvas';
import "./user_roles.scss";

const PAGE_TABS = [
  { id: "RRHH", label: "RRHH", icon: "👥" },
  { id: "RRHH Turnos", label: "RRHH Turnos", icon: "📅" },
  { id: "RRHH Solicitud s/Resul", label: "RRHH Solicitud s/Resul", icon: "📋" },
  { id: "RRHH c/ Resultados", label: "RRHH c/ Resultados", icon: "📊" },
  { id: "RRHH Turnos Postulantes", label: "RRHH Turnos Postulantes", icon: "👤" },
  { id: "RRHH Ausentismos", label: "RRHH Ausentismos", icon: "🏥" },
  { id: "ADM Ausentismos", label: "ADM Ausentismos", icon: "⚙️" },
];

const UsersRols = () => {
  const franchiseState = useSelector((state: RootState) => state.franchise);
  const hideAbsenteeism = isFranchiseTenant(franchiseState);
  const visibleTabs = filterAbsenteeismRoleTabs(PAGE_TABS, hideAbsenteeism);

  const [searchParams, setSearchParams] = useSearchParams("");
  const [currentTab, setCurrentTab] = useState(
    searchParams.get("currentTab") || visibleTabs[0]?.id || PAGE_TABS[0].id,
  );

  const handleChangeTab = (tab: string) => {
    if (hideAbsenteeism && isAbsenteeismReportTab(tab)) return;
    setCurrentTab(tab);
    setSearchParams({
      currentTab: tab,
    });
  };

  useEffect(() => {
    if (hideAbsenteeism && isAbsenteeismReportTab(currentTab)) {
      const fallback = visibleTabs[0]?.id || PAGE_TABS[0].id;
      setCurrentTab(fallback);
      setSearchParams({ currentTab: fallback });
    }
  }, [hideAbsenteeism, currentTab, visibleTabs, setSearchParams]);

  const getCurrentTabInfo = () => {
    return visibleTabs.find(tab => tab.id === currentTab) || visibleTabs[0];
  };

  const renderTabContent = () => {
    if (hideAbsenteeism && isAbsenteeismReportTab(currentTab)) {
      return <RRHH />;
    }
    switch (currentTab) {
      case "RRHH":
        return <RRHH />;
      case "RRHH Turnos":
        return <RRHHTurns />;
      case "RRHH Solicitud s/Resul":
        return <RRHHTurnSRes />;
      case "RRHH c/ Resultados":
        return <RRHHResult />;
      case "RRHH Turnos Postulantes":
        return <RRHHTurnsCandidates />;
      case "RRHH Ausentismos":
        return <RRHHAbsentees />;
      case "ADM Ausentismos":
        return <ADMAbsentees />;
      default:
        return <RRHH />;
    }
  };

  return (
    <div className="main-container">
      <div className="atendoo-card">
        {/* Header Section */}
        <div className="users-roles-header">
          <div className="header-top">
            <h6 className="subtitle">Gestión de Usuarios por Roles</h6>
            <InfoOffcanvas title="INFORMACIÓN DE ROLES">
              <div className="roles-info">
                <div className="role-section">
                  <h4 className="role-title">RRHH</h4>
                  <ul className="role-permissions">
                    <li>Cargar empleados y postulantes</li>
                    <li>Solicitar turnos</li>
                    <li>Solicitar ausentismo</li>
                    <li>Visualizar resultados</li>
                  </ul>
                </div>

                <div className="role-section">
                  <h4 className="role-title">RRHH Turnos</h4>
                  <ul className="role-permissions">
                    <li>Solicitar turnos</li>
                    <li>Visualizar resultados</li>
                  </ul>
                </div>

                <div className="role-section">
                  <h4 className="role-title">RRHH Solicitudes</h4>
                  <ul className="role-permissions">
                    <li>Solicitar turnos</li>
                    <li>Solicitar ausentismo</li>
                    <li>Sin tablero</li>
                  </ul>
                </div>

                <div className="role-section">
                  <h4 className="role-title">RRHH Resultados</h4>
                  <ul className="role-permissions">
                    <li>Visualizar resultados</li>
                    <li>Sin tablero</li>
                  </ul>
                </div>

                <div className="role-section">
                  <h4 className="role-title">RRHH Postulantes</h4>
                  <ul className="role-permissions">
                    <li>Cargar postulantes</li>
                    <li>Solicitar turnos de ingresos</li>
                    <li>Sin tablero</li>
                  </ul>
                </div>

                <div className="role-section">
                  <h4 className="role-title">RRHH Ausentismo</h4>
                  <ul className="role-permissions">
                    <li>Solicitar ausentismo</li>
                    <li>Visualizar resultados</li>
                    <li>Sin tablero</li>
                  </ul>
                </div>

                <div className="role-section">
                  <h4 className="role-title">ADM Ausentismo</h4>
                  <ul className="role-permissions">
                    <li>Solicitar ausentismo</li>
                    <li>Sin tablero</li>
                  </ul>
                </div>
              </div>
            </InfoOffcanvas>
          </div>
        </div>

        <div className="tabs-container mb-3">
          <nav className="tabs-nav flex-wrap justify-content-center">
            {visibleTabs.map((tab) => {
              return (
                <button
                  key={tab.id}
                  className={`tab-button ${currentTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleChangeTab(tab.id)}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span className="tab-label">{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>

        <div className="d-block mT-2 d-sm-none">
          <h4>{visibleTabs.find(tab => tab.id === currentTab)?.label}</h4>
        </div>

        {/* Tab Content */}
        <div className="tab-contentx">
          <div className="content-wrapper">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div >
  );
};

export default UsersRols;